import React, { useState, useEffect, useRef } from "react";
import dayjs from "dayjs";
import isSameOrBefore from "dayjs/plugin/isSameOrBefore";
import { collection, doc, getDoc, getDocs, getFirestore } from "firebase/firestore";
import { LocalizationProvider } from "@mui/x-date-pickers";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { Box, Container, Typography, Button, Table, TableBody, TableCell, TableHead, TableRow, Stack, IconButton } from "@mui/material";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import { useUser } from "../UserContext";
import BookingDialog from "./BookingDialog";
import ConfirmBookingPanel from "./ConfirmBookingPanel";

dayjs.extend(isSameOrBefore);

export default function CourtBookingMemberSide() {
  const db = getFirestore();
  const { user, memberData } = useUser();

  const [loading, setLoading] = useState(true);
  const [totalCourts, setTotalCourts] = useState(0);
  const [pennants, setPennants] = useState([]);
  const [bookings, setBookings] = useState([]);

  const [adultPrice30, setAdultPrice30] = useState(5);
  const [adultPrice60, setAdultPrice60] = useState(9);
  const [juniorPrice30, setJuniorPrice30] = useState(3);
  const [juniorPrice60, setJuniorPrice60] = useState(6);

  const [selectedDate, setSelectedDate] = useState(dayjs());

  const [timeSlots, setTimeSlots] = useState([]);

  const tableContainerRef = useRef(null);

  const [bookingDialogOpen, setBookingDialogOpen] = useState(false);
  const [selectedSlot, setSelectedSlot] = useState(null);

  const [tempBooking, setTempBooking] = useState(null);

  const dayMap = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"];

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {

        const settingsRef = doc(db, "pennantSettings", "settingsDoc");
        const settingsSnap = await getDoc(settingsRef);

        let fetchedCourts = 0;

        let fetchedAdult30 = 5;
        let fetchedAdult60 = 9;
        let fetchedJunior30 = 3;
        let fetchedJunior60 = 6;

        if (settingsSnap.exists()) {
          const data = settingsSnap.data();
          fetchedCourts = data.totalCourts || 0;
          fetchedAdult30 = data.adultPrice30 ?? 5;
          fetchedAdult60 = data.adultPrice60 ?? 9;
          fetchedJunior30 = data.juniorPrice30 ?? 3;
          fetchedJunior60 = data.juniorPrice60 ?? 6;
        }

        const pennantsSnap = await getDocs(collection(db, "Pennants"));
        const allPennants = pennantsSnap.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        }));

        const bookingsSnap = await getDocs(collection(db, "Bookings"));
        const allBookings = bookingsSnap.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        }));

        setTotalCourts(fetchedCourts);
        setPennants(allPennants);
        setBookings(allBookings);

        setAdultPrice30(fetchedAdult30);
        setAdultPrice60(fetchedAdult60);
        setJuniorPrice30(fetchedJunior30);
        setJuniorPrice60(fetchedJunior60);
      } catch (err) {
        console.error("Error loading data:", err);
      }
      setLoading(false);
    };
    fetchData();
  }, [db]);

  useEffect(() => {
    if (!loading) buildTimeSlots();
  }, [loading, selectedDate, pennants, bookings, totalCourts]);

  const buildTimeSlots = () => {
    const slots = [];
    const today = dayjs();
    let current;

    if (selectedDate.isSame(today, "day")) {
      const nowPlus5 = dayjs().add(5, "minute");

      const minute = nowPlus5.minute();
      const remainder = minute % 30;
      if (remainder !== 0) {
        current = nowPlus5.add(30 - remainder, "minute").second(0);
      } else {
        current = nowPlus5.second(0);
      }
    } else {
      current = selectedDate.startOf("day");
    }

    const endOfDay = selectedDate.endOf("day");

    while (current.add(30, "minute").isSameOrBefore(endOfDay)) {
      const start = current.clone();
      const next = current.add(30, "minute");
      const used = computeUsedCourts(start, next);
      const free = Math.max(totalCourts - used, 0);

      slots.push({
        startDayjs: start,
        endDayjs: next,
        startTime: start.format("HH:mm"),
        endTime: next.format("HH:mm"),
        displayRange: `${start.format("h:mm A")} - ${next.format("h:mm A")}`,
        used,
        free,
      });

      current = next;
    }
    setTimeSlots(slots);
  };

  const computeUsedCourts = (start, end) => {
    const slotStartMin = start.hour() * 60 + start.minute();
    const slotEndMin = end.hour() * 60 + end.minute();
    const dayOfWeekStr = dayMap[start.day()];
    let totalUsed = 0;

    pennants.forEach((p) => {
      if (!p.startDate || !p.endDate) return;
      const pStart = dayjs(p.startDate, "YYYY-MM-DD");
      const pEnd = dayjs(p.endDate, "YYYY-MM-DD");
      if (selectedDate.isBefore(pStart, "day") || selectedDate.isAfter(pEnd, "day")) return;
      if (dayOfWeekStr === p.pennantDay) {
        const [sh, sm] = (p.startTime || "00:00").split(":");
        const [eh, em] = (p.endTime || "00:00").split(":");
        const pStartMin = parseInt(sh, 10) * 60 + parseInt(sm, 10);
        const pEndMin = parseInt(eh, 10) * 60 + parseInt(em, 10);

        if (slotStartMin < pEndMin && pStartMin < slotEndMin) {
          totalUsed += parseInt(p.courtsRequired || "0", 10);
        }
      }
    });

    bookings.forEach((b) => {
      if (!b.date) return;
      const bDate = dayjs(b.date, "YYYY-MM-DD");
      if (!bDate.isSame(selectedDate, "day")) return;

      const [bSh, bSm] = (b.startTime || "00:00").split(":");
      const [bEh, bEm] = (b.endTime || "00:00").split(":");
      const bStartMin = parseInt(bSh, 10) * 60 + parseInt(bSm, 10);
      const bEndMin = parseInt(bEh, 10) * 60 + parseInt(bEm, 10);

      if (slotStartMin < bEndMin && bStartMin < slotEndMin) {
        totalUsed += parseInt(b.courtsUsed || "0", 10);
      }
    });

    return totalUsed;
  };

  const handleSlotClick = (slot) => {
    if (slot.free > 0) {
      setSelectedSlot(slot);
      setBookingDialogOpen(true);
    }
  };

  const handleCloseBookingDialog = () => {
    setSelectedSlot(null);
    setBookingDialogOpen(false);
  };

  const handleProceedToPayment = (tempBooking) => {
    setBookingDialogOpen(false);
    setTempBooking(tempBooking);
  };

  const handleCancelConfirmBooking = () => {
    setTempBooking(null);
  };

  const handlePaymentSuccess = () => {
    console.log("Payment success => reload or show success");
    setTempBooking(null);
  };

  const renderCourtTable = () => (
    <Box ref={tableContainerRef} sx={{ maxHeight: "600px", overflowY: "auto", mb: 1 }}>
      <Table>
        <TableHead>
          <TableRow>
            <TableCell>Time Range</TableCell>
            <TableCell align="center">Free Courts</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {timeSlots.map((slot, i) => {
            const bgColor = getSlotColor(slot.free);
            return (
              <TableRow
                key={i}
                sx={{ cursor: slot.free > 0 ? "pointer" : "default" }}
                style={{ backgroundColor: bgColor }}
                onClick={() => handleSlotClick(slot)}
              >
                <TableCell>{slot.displayRange}</TableCell>
                <TableCell align="center">{slot.free}</TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </Box>
  );

  const getSlotColor = (free) => {
    if (free === 0) return "lightgray";
    if (free === totalCourts) return "lightgreen";
    if (free <= 2) return "#FFB6C1";
    if (free < totalCourts / 2) return "orange";
    return "lightgreen";
  };

  if (loading) {
    return (
      <Container>
        <Typography variant="h5" sx={{ mt: 4 }}>
          Loading...
        </Typography>
      </Container>
    );
  }

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <Container>
        {tempBooking ? (
          <ConfirmBookingPanel
            bookingData={tempBooking}
            onCancel={handleCancelConfirmBooking}
            onSuccess={handlePaymentSuccess}
            adultPrice30={adultPrice30}
            adultPrice60={adultPrice60}
            juniorPrice30={juniorPrice30}
            juniorPrice60={juniorPrice60}
            memberData={memberData}
          />
        ) : (
          <Box sx={{ mt: 3 }}>
            <Typography variant="h4" gutterBottom>
              Book a Court
            </Typography>

            <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 2 }}>
              <Button
                variant="contained"
                onClick={() => setSelectedDate((prev) => prev.subtract(1, "day"))}
                disabled={selectedDate.isSame(dayjs(), "day")}
              >
                Prev Day
              </Button>
              <DatePicker
                label="Select Date"
                format="DD/MM/YYYY"
                value={selectedDate}
                onChange={(val) => val && setSelectedDate(val)}
                minDate={dayjs()}
              />
              <Button variant="contained" onClick={() => setSelectedDate((prev) => prev.add(1, "day"))}>
                Next Day
              </Button>
            </Stack>

            <Typography variant="subtitle1" sx={{ mb: 2 }}>
              Total Courts: {totalCourts}
            </Typography>

            <Stack direction="row" justifyContent="center" sx={{ mb: 1 }}>
              <IconButton
                color="primary"
                onClick={() =>
                  tableContainerRef.current?.scrollBy({ top: -200, behavior: "smooth" })
                }
              >
                <ArrowUpwardIcon />
              </IconButton>
            </Stack>

            {renderCourtTable()}

            <Stack direction="row" justifyContent="center" sx={{ mb: 3 }}>
              <IconButton
                color="primary"
                onClick={() =>
                  tableContainerRef.current?.scrollBy({ top: 200, behavior: "smooth" })
                }
              >
                <ArrowDownwardIcon />
              </IconButton>
            </Stack>
          </Box>
        )}

        <BookingDialog
          open={bookingDialogOpen}
          onClose={handleCloseBookingDialog}
          selectedSlot={selectedSlot}
          selectedDate={selectedDate}
          totalCourts={totalCourts}
          bookings={bookings}
          pennants={pennants}
          dayMap={dayMap}
          onProceedToPayment={handleProceedToPayment}
        />
      </Container>
    </LocalizationProvider>
  );
}
